import { NgRedux } from '@ptl/angular-redux-store';
import { Component } from '@angular/core';
import { FormComponentLookup, FormNavigationConfig, DefaultFormConfig, FormPageComponentClass } from '@ptl/forms';
import { PtlNotificationService, PtlNotificationStyle } from '@ptl/global-components';
import { StateStructure } from '@ptl/state';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { ScopeOfAppointment } from '../../../producer/interfaces/SOA/scope-of-appointment.interface';
import { SoaAction } from '../../../producer/interfaces/SOA/soa-action.interface';
import { SoaFormState, SoaScopeOfSalesData } from '../interfaces/soa.interface';
import { patchSoaState, resetSoaState } from '../reducer/soa.reducer';
import { SoaService } from '../services/soa.service';
import { REFERRAL_QUERY_PARAM_KEYS } from '../../../../shared/enums/query-params.enum';

@FormComponentLookup('page-soa-agent-confirmation')
@Component({
    selector: 'page-app-agent-confirmation',
    templateUrl: './page-agent-confirmation.component.html',
    styleUrls: ['./page-agent-confirmation.component.scss'],
    standalone: false
})
export class PageAgentConfirmationComponent extends FormPageComponentClass<FormNavigationConfig> {

  soaData: SoaScopeOfSalesData = {} as SoaScopeOfSalesData;

  invalidSoa = false;
  alreadySubmitted = false;
  isLoading = false;

  constructor(
    private soaService: SoaService,
    private ptlNotificationService: PtlNotificationService,
    protected ngRedux: NgRedux<StateStructure>) {
    super();
  }

  ngOnInit(): void {
    this.initPageConfig();

    this.soaData.soaTokenId = this.params.get(REFERRAL_QUERY_PARAM_KEYS.REFERRAL_ID_PARAM);

    if (!this.soaData.soaTokenId) {
      return;
    }

    this.soaService.getSingleAppointment(this.soaData.soaTokenId).then((response) => {
      // validate response
      let soa = response?.scopeOfAppointments ? response.scopeOfAppointments[0] : null;
      if (soa) {
        // If we have data in the agent section then this was already submitted so we can show the archived version
        let agentAction = soa.agentAction ? soa.agentAction[0] : null;
        if (agentAction) {
          this.configureEntriesForArchive(soa);
        }
        else {
          // check to make sure the customer section contains the confirmation form data from the public soa
          let customerAction = soa.customerAction ? soa.customerAction[0] : null;
          if (customerAction) {
            // set data to our object
            this.soaData = customerAction.soaObj;
          }
        }
      }

      // validate we have form data being pulled in
      if (!this.soaData.formValue) {
        this.invalidSoa = true
      }
      else {
        this.ngRedux.dispatch(resetSoaState());
        this.ngRedux.dispatch(patchSoaState(this.soaData.formValue));
      }
    })
    .catch((err) => this.invalidSoa = true);
  }

  submitHook(formConfig) {
    return new Promise((resolve, reject) => {
      // Build the payload - don't need to include customer values as those are already saved (disabled fields won't be included)
      let payload: SoaScopeOfSalesData = {
        soaTokenId: this.soaData.soaTokenId,
        entries: this.formConfig.pages[0].entries,
        formValue: this.pageFormGroups['soa'].value
      };

      this.isLoading = true;

      // Handle the response
      this.soaService.submitAgentSoa(payload).then((response) => {
        if (response?.message === 'Submitted Scope of Appointment.') {
          this.ptlNotificationService.open({
            title: 'Success',
            content: 'Your appointment has been confirmed',
            primaryButtonText: 'OK',
            notificationStyle: PtlNotificationStyle.SUCCESS
          });

          this.ngRedux.dispatch(resetSoaState());
          this.alreadySubmitted = true;
        }
        else if (response?.message === 'Scope of Appointment already been submitted.') {
          this.ngRedux.dispatch(resetSoaState());
          this.alreadySubmitted = true;
        }
        else {
          this.soaService.showGenericError();
        }

        resolve(true);
      })
      .catch(console.log)
      .finally(() => this.isLoading = false);
    });
  }

  configureEntriesForArchive(soa: ScopeOfAppointment) {
    this.alreadySubmitted = true;
    
    let agentAction = soa.agentAction ? soa.agentAction[0] : null;
    let customerAction = soa.customerAction ? soa.customerAction[0] : null;
    
    // We should always have these at this point but just in case
    if (!agentAction || !customerAction) {
      return;
    }

    // Filter out the customer fields from the agent section. They have less details and will be duplicates
    this.filterDuplicatesFromAgentEntries(agentAction);

    // Compile all entries together and disable them
    this.formConfig.pages[0].entries = [].concat(customerAction.soaObj.entries, agentAction.soaObj.entries);
    this.formConfig.pages[0].entries.forEach((entry) => {
      if (entry.formControlAttributes) {
        entry.formControlAttributes.disabled = true;
      }
    });

    // Compile the full form value from both parts
    this.soaData.formValue = Object.assign(agentAction.soaObj.formValue, customerAction.soaObj.formValue);
  }

  // To be as robust as possible, we will remove all entries starting at index 0
  // until we reach a true form control entry that is not disabled. That will mark the
  // start of the agent specific form values
  filterDuplicatesFromAgentEntries(agentAction: SoaAction) {
    let finalEntries = [];
    let startAdding = false;

    agentAction.soaObj.entries.forEach((entry) => {
      if (entry.formControlAttributes?.formControlName && !entry.formControlAttributes.disabled) {
        startAdding = true;
      }

      if (startAdding) {
        finalEntries.push(entry);
      }
    });

    agentAction.soaObj.entries = finalEntries;
  }
}
